import { Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { useGetEmployeeById } from '../../../services/react-query/employees';
import { useGetAllPreferences } from '../../../services/react-query/preferences';
import { useGetAllTaskTemplate } from '../../../services/react-query/task_templates/useGetAllTaskTemplate';
import { headerTypographyProps } from '../../../styles/sxStyling';
import { Preference, PREFERENCES_VALID_KEYS } from '../../../types/Preference';
import { TaskTemplate, TaskTemplateLink } from '../../../types/TaskTemplate';
import { Nullable } from '../../../types/utils';
import { uuidFinder } from '../../../utility/inputUtils';
import { deriveInitialStartCondition, shouldDisableSubmitForStartCondition, StartConditionType } from '../../../utility/startCondition';
import StartCondition, { IStartConditionProps } from '../../tasks/StartCondition';
import EmployeeSearchAutocomplete from '../EmployeeSearchAutocomplete/EmployeeSearchAutocomplete';

interface IAddTaskTemplateModalProps extends Omit<IStartConditionProps<ModalState>, 'task' | 'setTask' | 'conditionType' | 'onTypeChange' | 'onTypeChange'> {
  isOpen: boolean;
  onCancel: () => void;
  onAdd: (linkingData: TaskTemplateLink) => void;
  taskTemplateLink?: TaskTemplateLink | null;
}

type ModalState = Nullable<TaskTemplateLink>;

const BLANK_STATE: ModalState = {
  uuid: null,
  task_template_name: null,
  task_template_uuid: null,
  assigned_to_uuid: null,
  start_date: null,
  start_date_offset: null,
  depends_on_uuid: null,
  due_date_offset: null,
  task_type_uuid: null,
};

const VALIDATION_FIELDS = ['due_date_offset', 'task_template_uuid', 'task_type_uuid'];

export default function AddTaskTemplateModal({ taskTemplateLink, isOpen, onCancel, onAdd, ...rest }: IAddTaskTemplateModalProps) {
  const [modalState, setModalState] = useState<ModalState>(BLANK_STATE);
  const [startCondType, setStartCondType] = useState<StartConditionType>('manual');

  const { disabled } = useContext(DisableContext);
  const { data: taskTemplateData, isLoading: loadingTemplates } = useGetAllTaskTemplate({
    params: { per_page: 0 },
  });
  const { data: employeeData } = useGetEmployeeById({
    uuid: modalState.assigned_to_uuid || '',
    enabled: !!modalState.assigned_to_uuid,
  });
  const { data: preferenceData, isLoading: isGetAllPreferencesLoading } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.TASK_TYPE] } },
  });

  const taskTemplates = taskTemplateData?.data || [];
  const preferences = preferenceData || [];
  useEffect(() => {
    if (taskTemplateLink) {
      setModalState({
        ...taskTemplateLink,
      });
      setStartCondType(deriveInitialStartCondition(taskTemplateLink));
    } else {
      setModalState(BLANK_STATE);
      setStartCondType(deriveInitialStartCondition(BLANK_STATE));
    }
  }, [isOpen, taskTemplateLink]);
  const startCondInvalid = shouldDisableSubmitForStartCondition(modalState, startCondType);
  const formInvalid =
    startCondInvalid ||
    _.some(_.values(_.pick(modalState, VALIDATION_FIELDS)), _.isNull) ||
    _.some([modalState.start_date_offset, modalState.due_date_offset], Number.isNaN);

  const handleSubmit = () => {
    if (formInvalid) return;
    onAdd({
      ...(modalState as Omit<TaskTemplateLink, 'uuid'>),
      uuid: taskTemplateLink?.uuid || crypto.randomUUID(),
    });
    onCancel();
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} fullWidth>
      <Typography {...headerTypographyProps} sx={{ m: 2 }}>
        Please choose which task template to add
      </Typography>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <Autocomplete<TaskTemplate>
          loading={loadingTemplates}
          options={taskTemplates}
          disabled={!!taskTemplateLink}
          onChange={(_, template) =>
            setModalState({
              ...modalState,
              task_template_uuid: template?.uuid || null,
              task_template_name: template?.name || null,
            })
          }
          getOptionLabel={(template) => template?.name || ''}
          value={
            taskTemplates.length > 0 && modalState?.task_template_uuid
              ? (_.find(taskTemplates, { uuid: modalState.task_template_uuid }) as TaskTemplate | null)
              : null
          }
          renderInput={(params) => <TextField {...params} autoFocus label="Task Templates" />}
        />
        <EmployeeSearchAutocomplete
          isDisabled={disabled}
          value={employeeData ? employeeData : null}
          onChange={(employee) => {
            setModalState({
              ...modalState,
              assigned_to_uuid: employee?.uuid || null,
              assigned_to_display_name: employee?.display_name || null,
            });
          }}
          label="Assign To"
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Autocomplete<Preference>
            fullWidth
            loading={isGetAllPreferencesLoading}
            options={_.filter(preferences, { key: PREFERENCES_VALID_KEYS.TASK_TYPE })}
            onChange={(_, preference) => setModalState({ ...modalState, task_type_uuid: preference?.uuid, task_type_value: preference?.value })}
            getOptionLabel={(preference) => preference.value}
            value={uuidFinder(preferences, modalState.task_type_uuid || '')}
            renderInput={(params) => <TextField {...params} label="Task Type" />}
          />
          <TextField
            fullWidth
            label="Due Date Offset"
            type="number"
            value={modalState?.due_date_offset ?? ''}
            onChange={(event) => setModalState({ ...modalState, due_date_offset: event.target.value ? Number(event.target.value) : null })}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <StartCondition task={modalState} setTask={setModalState} onTypeChange={setStartCondType} conditionType={startCondType} {...rest} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={formInvalid} onClick={handleSubmit}>
          {taskTemplateLink ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
